import React, {MutableRefObject, useCallback} from 'react';
import * as Yup from 'yup';
import {Form as FormikForm, Formik} from 'formik';
import {FormikHelpers, FormikProps, FormikValues} from 'formik/dist/types';
import {IComponentWithChildren} from 'models';

interface IFormProps<T> extends IComponentWithChildren {
  initialValues: T;
  validationSchema: Yup.ObjectSchema;
  submitHandler: (values: T, actions: FormikHelpers<T>) => void;
  validateOnMount?: boolean;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  enableReinitialize?: boolean;
  /**
   * When true, Enter / adornment IconButtons cannot submit the form.
   * Submit only via Formik submitForm() (e.g. Upload / Save buttons).
   */
  disableNativeSubmit?: boolean;
  formikRef?: MutableRefObject<FormikProps<FormikValues>>;
}

export function Form<T>({
  initialValues,
  validationSchema,
  submitHandler,
  children,
  validateOnMount = false,
  validateOnChange = true,
  validateOnBlur = false,
  enableReinitialize = true,
  disableNativeSubmit = false,
  formikRef,
}: IFormProps<T>) {
  const blockNativeSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return (
    <Formik
      enableReinitialize={enableReinitialize}
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      validateOnChange={validateOnChange}
      validateOnMount={validateOnMount}
      validateOnBlur={validateOnBlur}
    >
      {disableNativeSubmit ? (
        // Plain form so we fully own onSubmit and ignore Enter / default button submits.
        // Upload / Save still work via Formik submitForm().
        <form style={{width: '100%'}} onSubmit={blockNativeSubmit} noValidate>
          {children}
        </form>
      ) : (
        <FormikForm style={{width: '100%'}}>{children}</FormikForm>
      )}
    </Formik>
  );
}
